<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import MediaLayer from './MediaLayer.vue'
import { usePlayer, type LayerSlot } from '@/composables/usePlayer'
import type { RuntimeConfig } from '@/config'
import { resolveStageBox } from '@/lib/stage'
import type { Playlist } from '@/types'

const props = defineProps<{
  config: RuntimeConfig
  playlist: Playlist | null
}>()

const layer0 = ref<InstanceType<typeof MediaLayer> | null>(null)
const layer1 = ref<InstanceType<typeof MediaLayer> | null>(null)

const player = usePlayer({
  config: props.config,
  playlist: computed(() => props.playlist),
  getLayer: (slot: LayerSlot) => (slot === 0 ? layer0.value : layer1.value),
})

const { activeSlot, retiringSlot, transitionMs } = player

const viewportWidth = ref(window.innerWidth)
const viewportHeight = ref(window.innerHeight)

/**
 * Content is composed for the screen's aspect ratio rather than the viewport, so
 * a portrait layout stays intact on a landscape desktop instead of being cropped.
 */
const stageBox = computed(() =>
  resolveStageBox(viewportWidth.value, viewportHeight.value, props.config.aspect),
)

const stageStyle = computed(() => ({
  width: `${stageBox.value.width}px`,
  height: `${stageBox.value.height}px`,
  left: `${stageBox.value.left}px`,
  top: `${stageBox.value.top}px`,
}))

function measureViewport(): void {
  viewportWidth.value = window.innerWidth
  viewportHeight.value = window.innerHeight
}

function isVisible(slot: LayerSlot): boolean {
  return activeSlot.value === slot || retiringSlot.value === slot
}

watch(
  () => props.playlist,
  () => player.refresh(),
)

onMounted(() => {
  measureViewport()
  window.addEventListener('resize', measureViewport)
  window.addEventListener('orientationchange', measureViewport)
  player.start()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', measureViewport)
  window.removeEventListener('orientationchange', measureViewport)
  player.stop()
})

defineExpose({ snapshot: player.snapshot, hasContent: player.hasContent, stageBox })
</script>

<template>
  <div class="frame">
    <div class="stage" :style="stageStyle">
      <MediaLayer
        ref="layer0"
        :visible="isVisible(0)"
        :elevated="activeSlot === 0"
        :transition-ms="transitionMs"
        :muted="!config.sound"
        :fit-override="config.fitOverride"
        :prepare-timeout-ms="config.prepareTimeoutMs"
        :first-frame-timeout-ms="config.firstFrameTimeoutMs"
      />
      <MediaLayer
        ref="layer1"
        :visible="isVisible(1)"
        :elevated="activeSlot === 1"
        :transition-ms="transitionMs"
        :muted="!config.sound"
        :fit-override="config.fitOverride"
        :prepare-timeout-ms="config.prepareTimeoutMs"
        :first-frame-timeout-ms="config.firstFrameTimeoutMs"
      />
    </div>
  </div>
</template>

<style scoped>
.frame {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #000;
}

.stage {
  position: absolute;
  overflow: hidden;
  background: #000;
}
</style>
